import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Avatar,
  TablePagination,
  Tooltip,
  Divider,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  Person,
  Phone,
  LocationOn,
  Business,
  CalendarMonth,
  Public,
  Groups,
  Share,
  Recommend,
  Close,
  ExpandMore,
} from "@mui/icons-material";
import { clienteService } from "../../services/clienteService";
import { formatDateShort } from "../../utils/dateUtils";


// ...existing code...
const CANALES = [
  {
    value: "redes sociales",
    label: "Redes Sociales",
    icon: <Share fontSize="small" />,
  },
  {
    value: "familiares",
    label: "Familiares",
    icon: <Groups fontSize="small" />,
  },
  {
    value: "referencias",
    label: "Referencias",
    icon: <Recommend fontSize="small" />,
  },
  {
    value: "pagina web",
    label: "Página Web",
    icon: <Public fontSize="small" />,
  },
];

const CanalChip = ({ canal }) => {
  const canalInfo = CANALES.find((c) => c.value === canal) || {
    label: canal || "No especificado",
    icon: <Public fontSize="small" />,
  };

  const getColor = () => {
    switch (canal) {
      case "redes sociales":
        return { bg: "#e3f2fd", color: "#1565c0" };
      case "familiares":
        return { bg: "#fff3e0", color: "#ef6c00" };
      case "referencias":
        return { bg: "#e8f5e9", color: "#2e7d32" };
      case "pagina web":
        return { bg: "#f3e5f5", color: "#7b1fa2" };
      default:
        return { bg: "#f5f5f5", color: "#616161" };
    }
  };

  const colors = getColor();

  return (
    <Chip
      label={canalInfo.label}
      icon={canalInfo.icon}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        fontWeight: 600,
        "& .MuiChip-icon": { color: colors.color },
      }}
    />
  );
};

const AdminClientes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog State
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  // Modal de visualización de cliente
  const [viewModalOpen, setViewModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedCliente, setSelectedCliente] = useState(null);
  // Tarjeta expandida en móvil
  const [expandedCliente, setExpandedCliente] = useState(null);

  // Original data for change detection
  const [originalData, setOriginalData] = useState(null);

  // Form State
  const initialForm = {
    nombre: "",
    telefono: "",
    direccion: "",
    canal: "pagina web",
    cliente_de: "",
  };
  const [formData, setFormData] = useState(initialForm);

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await clienteService.getAll();
      const list = data?.clientes || data?.data || [];
      setClientes(Array.isArray(list) ? list : []);
      setError("");
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "Error al cargar clientes";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Detectar si hay cambios en el formulario
  const hasChanges = useMemo(() => {
    if (!isEdit) return true;
    if (!originalData) return false;

    const fieldsToCompare = [
      "nombre",
      "telefono",
      "direccion",
      "canal",
      "cliente_de",
    ];
    const normalize = (val) =>
      val === null || val === undefined ? "" : String(val).trim();

    return fieldsToCompare.some(
      (field) => normalize(formData[field]) !== normalize(originalData[field]),
    );
  }, [isEdit, originalData, formData]);

  // Handlers
  const handleOpen = (cliente = null) => {
    if (cliente) {
      setIsEdit(true);
      setCurrentId(cliente.id);
      const data = {
        nombre: cliente.nombre || "",
        telefono: cliente.telefono || "",
        direccion: cliente.direccion || "",
        canal: cliente.canal || "pagina web",
        cliente_de: cliente.cliente_de || "",
      };
      setFormData({ ...data });
      setOriginalData({ ...data });
    } else {
      setIsEdit(false);
      setCurrentId(null);
      setFormData(initialForm);
      setOriginalData(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialForm);
    setOriginalData(null);
  };

  const handleDelete = async (cliente) => {
    if (
      window.confirm(
        `¿Estás seguro de eliminar al cliente "${cliente.nombre}"?`,
      )
    ) {
      try {
        await clienteService.delete(cliente.id);
        fetchData();
      } catch (err) {
        const msg = err.response?.data?.message || "Error al eliminar cliente";
        alert(msg);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      alert("El nombre del cliente es requerido");
      return;
    }

    try {
      if (isEdit) {
        await clienteService.update({ ...formData, id: currentId });
        alert("✓ Cliente actualizado correctamente");
      } else {
        await clienteService.create(formData);
        alert("✓ Cliente creado exitosamente");
      }
      handleClose();
      fetchData();
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Error desconocido";
      alert("❌ Error al guardar: " + msg);
    }
  };

  // Filter & Pagination
  const filteredClientes = clientes.filter(
    (cliente) =>
      cliente.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      cliente.telefono?.toLowerCase().includes(search.toLowerCase()) ||
      cliente.direccion?.toLowerCase().includes(search.toLowerCase()) ||
      cliente.cliente_de?.toLowerCase().includes(search.toLowerCase()),
  );

  // Stats
  const stats = {
    total: clientes.length,
    redesSociales: clientes.filter((c) => c.canal === "redes sociales").length,
    referencias: clientes.filter((c) => c.canal === "referencias").length,
    paginaWeb: clientes.filter((c) => c.canal === "pagina web").length,
  };

  // Usar utilidad centralizada de fechas con zona horaria CDMX
  const formatDate = formatDateShort;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, overflowX: "hidden", width: "100%", boxSizing: "border-box" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.25rem", sm: "2.125rem" },
          }}
        >
          Gestión de Clientes
        </Typography>
        {/* Botón desktop */}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
          sx={{
            display: { xs: "none", sm: "flex" },
            background: "linear-gradient(45deg, #1a237e 30%, #283593 90%)",
            boxShadow: "0 3px 5px 2px rgba(26, 35, 126, .3)",
            borderRadius: 2,
          }}
        >
          Nuevo Cliente
        </Button>
        {/* Botón móvil - Icono circular */}
        <IconButton
          onClick={() => handleOpen()}
          sx={{
            display: { xs: "flex", sm: "none" },
            width: 44,
            height: 44,
            background: "linear-gradient(45deg, #1a237e 30%, #283593 90%)",
            color: "#fff",
            boxShadow: "0 3px 8px rgba(26, 35, 126, .4)",
            "&:hover": {
              background: "linear-gradient(45deg, #283593 30%, #3949ab 90%)",
            },
          }}
        >
          <Add />
        </IconButton>
      </Box>

      {/* Stats Cards - solo desktop, renderizado condicional para no contaminar el layout móvil */}
      {!isMobile && (
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
          {[
            { label: "Total", value: stats.total, color: "#1a237e", icon: <Person /> },
            { label: "Redes", value: stats.redesSociales, color: "#1565c0", icon: <Share /> },
            { label: "Referencias", value: stats.referencias, color: "#2e7d32", icon: <Recommend /> },
            { label: "Web", value: stats.paginaWeb, color: "#7b1fa2", icon: <Public /> },
          ].map((stat, index) => (
            <Grid item sm={3} key={index} sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: "linear-gradient(145deg, rgba(19, 24, 41, 0.95) 0%, rgba(30, 36, 60, 0.95) 100%)",
                  border: `2px solid ${stat.color}40`,
                  boxShadow: `0 4px 20px ${stat.color}30`,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "scale(1.05)", boxShadow: `0 6px 25px ${stat.color}50` },
                }}
              >
                <Avatar sx={{ bgcolor: stat.color + "30", color: stat.color, width: 36, height: 36, mb: 0.5 }}>
                  {React.cloneElement(stat.icon, { sx: { fontSize: 20 } })}
                </Avatar>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", textAlign: "center" }}>
                  {stat.label}
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: stat.color, fontSize: "1.25rem" }}>
                  {stat.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Stats círculos móvil */}
      {isMobile && (
        <Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
          {[
            { label: "Redes", value: stats.redesSociales, color: "#1565c0", icon: <Share /> },
            { label: "Referencias", value: stats.referencias, color: "#2e7d32", icon: <Recommend /> },
            { label: "Web", value: stats.paginaWeb, color: "#7b1fa2", icon: <Public /> },
          ].map((stat, index) => (
            <Grid item xs={4} key={index} sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  background: "linear-gradient(145deg, rgba(19,24,41,0.95) 0%, rgba(30,36,60,0.95) 100%)",
                  border: `2px solid ${stat.color}40`,
                  boxShadow: `0 4px 20px ${stat.color}30`,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "scale(1.05)", boxShadow: `0 6px 25px ${stat.color}50` },
                }}
              >
                <Avatar sx={{ bgcolor: stat.color + "30", color: stat.color, width: 30, height: 30, mb: 0.25 }}>
                  {React.cloneElement(stat.icon, { sx: { fontSize: 16 } })}
                </Avatar>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.6rem", textAlign: "center", lineHeight: 1.2 }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ color: stat.color, fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.2 }}>
                  {stat.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Buscador móvil */}
      {isMobile && (
        <Box sx={{ mb: 2 }}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Buscar clientes..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "rgba(255,255,255,0.45)", fontSize: 22 }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "50px",
                color: "#fff",
                background: "rgba(255,255,255,0.05)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)", borderRadius: "50px" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.35)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6B8EFF" },
                "& input::placeholder": { color: "rgba(255,255,255,0.4)", opacity: 1 },
                "& input": { py: 1.4 },
              },
            }}
          />
        </Box>
      )}

      {/* Lista de clientes móvil — accordion */}
      {isMobile && (
        <Box>
          {loading ? (
            <Typography sx={{ textAlign: "center", color: "rgba(255,255,255,0.4)", py: 4 }}>Cargando...</Typography>
          ) : filteredClientes.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "rgba(255,255,255,0.4)", py: 4 }}>No hay clientes registrados</Typography>
          ) : (
            filteredClientes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((cliente) => {
              const isExpanded = expandedCliente === cliente.id;
              const iniciales = (cliente.nombre || "?").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
              const canalColor = cliente.canal === "pagina web" ? "#7b1fa2" : cliente.canal === "redes sociales" ? "#1565c0" : cliente.canal === "referencias" ? "#2e7d32" : "#1976d2";
              return (
                <Box
                  key={cliente.id}
                  sx={{
                    mb: 1.5,
                    borderRadius: "20px",
                    background: "linear-gradient(145deg, rgba(19,24,41,0.97) 0%, rgba(26,31,52,0.97) 100%)",
                    border: `1px solid ${isExpanded ? "rgba(107,142,255,0.35)" : "rgba(255,255,255,0.07)"}`,
                    overflow: "hidden",
                    transition: "border-color .2s",
                  }}
                >
                  {/* Fila principal */}
                  <Box sx={{ display: "flex", alignItems: "center", p: 1.5, gap: 1.5 }}>
                    {/* Avatar — clic para expandir */}
                    <Avatar
                      onClick={() => setExpandedCliente(isExpanded ? null : cliente.id)}
                      sx={{
                        width: 48, height: 48, flexShrink: 0,
                        bgcolor: canalColor + "30",
                        color: canalColor,
                        fontWeight: 800, fontSize: "1rem",
                        border: `2px solid ${canalColor}60`,
                        cursor: "pointer",
                        transition: "transform .15s, box-shadow .15s",
                        "&:hover": { transform: "scale(1.08)", boxShadow: `0 0 12px ${canalColor}50` },
                      }}
                    >
                      {iniciales}
                    </Avatar>
                    {/* Nombre */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{ fontWeight: 700, color: "#fff", fontSize: "0.95rem", lineHeight: 1.3 }}
                        noWrap
                      >
                        {cliente.nombre}
                      </Typography>
                      {cliente.cliente_de && (
                        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.45)", lineHeight: 1.2 }} noWrap>
                          {cliente.cliente_de}
                        </Typography>
                      )}
                    </Box>
                    {/* Flecha expand */}
                    <ExpandMore
                      onClick={() => setExpandedCliente(isExpanded ? null : cliente.id)}
                      sx={{
                        color: "rgba(255,255,255,0.4)",
                        fontSize: 22,
                        flexShrink: 0,
                        cursor: "pointer",
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform .25s",
                      }}
                    />
                  </Box>

                  {/* Panel expandido */}
                  <Collapse in={isExpanded}>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.07)" }} />
                    <Box sx={{ p: 2, pt: 1.5, display: "flex", flexDirection: "column", gap: 1.2 }}>
                      {/* Teléfono */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Phone sx={{ fontSize: 17, color: "rgba(255,255,255,0.4)", mt: 0.15, flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)", lineHeight: 1 }}>Teléfono</Typography>
                          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>{cliente.telefono || "-"}</Typography>
                        </Box>
                      </Box>
                      {/* Dirección */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <LocationOn sx={{ fontSize: 17, color: "rgba(255,255,255,0.4)", mt: 0.15, flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)", lineHeight: 1 }}>Dirección</Typography>
                          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.8)" }}>{cliente.direccion || "-"}</Typography>
                        </Box>
                      </Box>
                      {/* Canal */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Share sx={{ fontSize: 17, color: "rgba(255,255,255,0.4)", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)", lineHeight: 1 }}>Canal</Typography>
                          <CanalChip canal={cliente.canal} />
                        </Box>
                      </Box>
                      {/* Registro */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarMonth sx={{ fontSize: 17, color: "rgba(255,255,255,0.4)", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)", lineHeight: 1 }}>Registro</Typography>
                          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.8)" }}>{formatDate(cliente.fecha_creacion)}</Typography>
                        </Box>
                      </Box>
                      {/* Acciones */}
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpen(cliente)}
                          sx={{ color: "#6B8EFF", bgcolor: "rgba(107,142,255,0.1)", borderRadius: "10px", px: 1.5, "&:hover": { bgcolor: "rgba(107,142,255,0.2)" } }}
                        >
                          <Edit fontSize="small" />
                          <Typography sx={{ fontSize: "0.75rem", ml: 0.5 }}>Editar</Typography>
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(cliente)}
                          sx={{ color: "#FF6B6B", bgcolor: "rgba(255,107,107,0.1)", borderRadius: "10px", px: 1.5, "&:hover": { bgcolor: "rgba(255,107,107,0.2)" } }}
                        >
                          <Delete fontSize="small" />
                          <Typography sx={{ fontSize: "0.75rem", ml: 0.5 }}>Eliminar</Typography>
                        </IconButton>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })
          )}
          {/* Paginación móvil */}
          {filteredClientes.length > rowsPerPage && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredClientes.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              labelRowsPerPage=""
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "rgba(255,255,255,0.5)",
                "& .MuiTablePagination-toolbar": { justifyContent: "center", flexWrap: "wrap" },
                "& .MuiTablePagination-selectLabel": { display: "none" },
                "& .MuiTablePagination-select": { display: "none" },
                "& .MuiTablePagination-selectIcon": { display: "none" },
              }}
            />
          )}
        </Box>
      )}
      {/* Vista desktop — tabla en Paper */}
      {!isMobile && (
      /* ══════════════════════════════════════
         VISTA DESKTOP — tabla en Paper
         ══════════════════════════════════════ */
      <Paper sx={{ borderRadius: 3, overflow: "hidden", boxShadow: 3 }}>
        {/* Toolbar desktop */}
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid #eee" }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar por nombre, teléfono, dirección..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment>,
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
            {filteredClientes.length} cliente(s)
          </Typography>
        </Box>

        {/* Tabla desktop */}
        <>
          <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                <colgroup>
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "8%" }} />
                </colgroup>
                <TableHead sx={{ backgroundColor: "#1a237e" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#e5e7eb", borderBottom: "none" }}>
                      Cliente
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#e5e7eb", borderBottom: "none" }}>
                      Teléfono
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#e5e7eb", borderBottom: "none" }}>
                      Dirección
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#e5e7eb", borderBottom: "none" }}>
                      Canal
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#e5e7eb", borderBottom: "none" }}>
                      Cliente de
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#e5e7eb", borderBottom: "none" }}>
                      Registro
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", color: "#e5e7eb", borderBottom: "none" }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClientes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((cliente) => (
                      <TableRow key={cliente.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: "#1a237e", width: 32, height: 32, fontSize: "0.85rem", flexShrink: 0 }}>
                              {cliente.nombre?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="subtitle2" fontWeight="bold" noWrap>
                              {cliente.nombre}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Phone fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                            <Typography variant="body2" noWrap>{cliente.telefono || "-"}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LocationOn fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                            <Typography variant="body2" noWrap title={cliente.direccion}>
                              {cliente.direccion || "-"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <CanalChip canal={cliente.canal} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>{cliente.cliente_de || "-"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>{formatDate(cliente.fecha_creacion)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                            <Tooltip title="Editar">
                              <IconButton size="small" color="primary" onClick={() => handleOpen(cliente)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" color="error" onClick={() => handleDelete(cliente)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredClientes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <Typography color="text.secondary">
                          No se encontraron clientes
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredClientes.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
      </Paper>
      )}

      {/* Modal Form */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(45deg, #1a237e 30%, #283593 90%)",
            color: "white",
          }}
        >
          {isEdit ? "Editar Cliente" : "Nuevo Cliente"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          <form id="cliente-form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre del Cliente"
                  required
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={formData.telefono}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      telefono: e.target.value.replace(/\D/g, "").slice(0, 10),
                    })
                  }
                  inputProps={{ maxLength: 10, inputMode: "numeric" }}
                  helperText={
                    formData.telefono && formData.telefono.length < 10
                      ? `${formData.telefono.length}/10 dígitos`
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Canal de Captación"
                  value={formData.canal}
                  onChange={(e) =>
                    setFormData({ ...formData, canal: e.target.value })
                  }
                >
                  {CANALES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {option.icon}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Dirección"
                  multiline
                  rows={2}
                  value={formData.direccion}
                  onChange={(e) =>
                    setFormData({ ...formData, direccion: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cliente de (Referido por)"
                  value={formData.cliente_de}
                  onChange={(e) =>
                    setFormData({ ...formData, cliente_de: e.target.value })
                  }
                  placeholder="Nombre de quien lo refirió"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            variant="contained"
            type="submit"
            form="cliente-form"
            color="primary"
            disabled={!formData.nombre || (isEdit && !hasChanges)}
            title={
              isEdit
                ? hasChanges
                  ? "Guardar cambios realizados"
                  : "No hay cambios para guardar"
                : "Completa los campos para guardar"
            }
          >
            {isEdit ? "Guardar Cambios" : "Crear Cliente"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualización de Cliente (solo móvil) */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(145deg, #1a1f35 0%, #0d1117 100%)",
            borderRadius: 3,
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        {selectedCliente && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pb: 1,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Detalles del Cliente
              </Typography>
              <IconButton
                onClick={() => setViewModalOpen(false)}
                sx={{ color: "text.secondary" }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {/* Header con Avatar y Nombre */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 3,
                  pb: 2,
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 1.5,
                    bgcolor: "#1976d2",
                    fontSize: "1.5rem",
                  }}
                >
                  {(
                    selectedCliente.nombre ||
                    selectedCliente.nombre_completo ||
                    "C"
                  )
                    .split(" ")
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </Avatar>
                <Typography variant="h6" fontWeight="bold" textAlign="center">
                  {selectedCliente.nombre || selectedCliente.nombre_completo}
                </Typography>
                <CanalChip canal={selectedCliente.canal} />
              </Box>

              {/* Información detallada */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Phone sx={{ color: "#4caf50" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Teléfono
                    </Typography>
                    <Typography variant="body2">
                      {selectedCliente.telefono || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <LocationOn sx={{ color: "#ff9800" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Dirección
                    </Typography>
                    <Typography variant="body2">
                      {selectedCliente.direccion || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Share sx={{ color: "#2196f3" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Canal de Adquisición
                    </Typography>
                    <Typography variant="body2">
                      {selectedCliente.canal || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Business sx={{ color: "#9c27b0" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Cliente de
                    </Typography>
                    <Typography variant="body2">
                      {selectedCliente.cliente_de || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CalendarMonth sx={{ color: "#00bcd4" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Fecha de Registro
                    </Typography>
                    <Typography variant="body2">
                      {formatDateShort(selectedCliente.fecha_creacion)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => {
                  setViewModalOpen(false);
                  handleOpen(selectedCliente);
                }}
              >
                Editar
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<Delete />}
                onClick={() => {
                  setViewModalOpen(false);
                  handleDelete(selectedCliente.id);
                }}
              >
                Eliminar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AdminClientes;
